// ANA-R3-GEOM-05 — the on-tile canvas CoordinateTransform.
//
// The tile <canvas> shows a SQUARE source-region crop (TILE-03's
// `computeTileCrop`): the source region `[sx, sy, sx+cropSide, sy+cropSide]`
// is drawn to a `tileDisplayPx × tileDisplayPx` cell. The PolygonEditor's
// geometry is NORMALISED 0..1 in IMAGE space; it needs a `CoordinateTransform`
// to map a normalised-image point → tile screen px (and the inverse).
//
// Base mapping (no local zoom/pan), forward:
//   normalised image (nx,ny)
//     → source px:     (nx*imgW, ny*imgH)
//     → crop-relative: (− sx, − sy)
//     → tile px:       (× tileDisplayPx/cropSide)
// Inverse (screen → image) runs the chain backwards.
//
// LOCAL MINI-VIEWPORT. Editing on a small (270px) tile is imprecise, so we
// layer a local mousewheel-zoom + pan on TOP of the base crop: a scale
// `editZoom` about a pan focus. Screen point `s` maps to the "base tile px"
// `b` via `b = (s − center)/editZoom + center − pan`, where `center` is the
// tile midpoint (so wheel-zoom keeps the cursor anchored) and `pan` shifts the
// view. The base px then inverts through the crop chain to image space. This
// is self-contained: OSD is NOT involved (unlike GEOM-03/04's viewer transform).
//
// All math is framework-free + pure so it is unit-testable without a canvas.
import type { CoordinateTransform, Point } from "@aeriosiq/ui";

export interface TileCropFrame {
  /** Square source-crop side in px (TILE-03 `computeTileCrop.cropSide`). */
  cropSide: number;
  /** Top-left source x of the crop (`computeTileCrop.sx`). */
  sx: number;
  /** Top-left source y of the crop (`computeTileCrop.sy`). */
  sy: number;
  /** Source image width in px. */
  imgW: number;
  /** Source image height in px. */
  imgH: number;
  /** Logical tile display side in CSS px (the chosen preset). */
  tileDisplayPx: number;
}

export interface TileLocalView {
  /** Local mini-viewport magnification (1 = the base crop, >1 = zoomed in). */
  editZoom: number;
  /** Local pan in BASE-tile px (added after the zoom-about-centre). */
  panX: number;
  panY: number;
  /**
   * Monotonic version token — bump on any zoom/pan/resize so the editor
   * re-projects its handles (CoordinateTransform contract).
   */
  version: number;
}

// ANA-R4-TILE-ZOOM-OUT — the editor allows zooming OUT below the base crop
// (editZoom < 1) so the analyst can see more surrounding context, not just in.
export const TILE_EDIT_MIN_ZOOM = 0.25;
export const TILE_EDIT_MAX_ZOOM = 12;

export function clampEditZoom(z: number): number {
  if (!Number.isFinite(z)) return TILE_EDIT_MIN_ZOOM;
  return Math.max(TILE_EDIT_MIN_ZOOM, Math.min(TILE_EDIT_MAX_ZOOM, z));
}

/** normalised image point → BASE tile px (no local zoom/pan). */
function imageToBaseTile(p: Point, f: TileCropFrame): { x: number; y: number } {
  const scale = f.tileDisplayPx / f.cropSide;
  return {
    x: (p.x * f.imgW - f.sx) * scale,
    y: (p.y * f.imgH - f.sy) * scale,
  };
}

/** BASE tile px → normalised image point (inverse of {@link imageToBaseTile}). */
function baseTileToImage(b: { x: number; y: number }, f: TileCropFrame): Point {
  const scale = f.tileDisplayPx / f.cropSide;
  const sxPx = b.x / scale + f.sx;
  const syPx = b.y / scale + f.sy;
  return {
    x: f.imgW === 0 ? 0 : sxPx / f.imgW,
    y: f.imgH === 0 ? 0 : syPx / f.imgH,
  };
}

/**
 * Build the editor's `CoordinateTransform` for one tile: the TILE-03 crop chain
 * composed with the local mousewheel-zoom + pan mini-viewport. `view` defaults
 * to the identity (no zoom/pan) so a static tile still works.
 *
 * The zoom is "about the tile centre": at `editZoom = z` and pan `(panX,panY)`,
 *   screen = (base + pan − centre) * z + centre
 *   base   = (screen − centre)/z − pan + centre
 * which keeps handles at constant SCREEN size (the editor sizes them in screen
 * px) and lets a wheel-zoom keep the cursor anchored (the host adjusts `pan`).
 */
export function createTileTransform(
  frame: TileCropFrame,
  view: TileLocalView = { editZoom: 1, panX: 0, panY: 0, version: 0 },
): CoordinateTransform {
  const centre = frame.tileDisplayPx / 2;
  const z = clampEditZoom(view.editZoom);

  return {
    imageToScreen: (p: Point) => {
      const b = imageToBaseTile(p, frame);
      return {
        x: (b.x + view.panX - centre) * z + centre,
        y: (b.y + view.panY - centre) * z + centre,
      };
    },
    screenToImage: (s) => {
      const b = {
        x: (s.x - centre) / z - view.panX + centre,
        y: (s.y - centre) / z - view.panY + centre,
      };
      return baseTileToImage(b, frame);
    },
    version: view.version,
  };
}

/**
 * ANA-R4-TILE-02 — the maximum local `editZoom` the user may reach so that the
 * EFFECTIVE on-tile magnification never exceeds the Tiles "max zoom" setting
 * (`maxZoomPercent`, % of native res). The base crop already shows the defect at
 * `baseMag% = tileDisplayPx / cropSide × 100`, clamped at ≤ maxZoomPercent by
 * `computeTileCrop`. `editZoom` multiplies on top, so the cap is
 *   editZoomMax = maxZoomPercent / baseMag% = maxZoomPercent × cropSide /
 *                 (tileDisplayPx × 100)
 * floored at 1 (you can always sit at the base crop). A tiny defect already
 * framed at the cap gets editZoomMax = 1 — no further zoom-in, as required.
 */
export function editZoomMaxForCap(
  frame: TileCropFrame,
  maxZoomPercent: number,
): number {
  if (frame.tileDisplayPx <= 0 || maxZoomPercent <= 0) return 1;
  const cap = (maxZoomPercent * frame.cropSide) / (frame.tileDisplayPx * 100);
  // The max-zoom cap is always >= 1 (you can always sit at the base crop); zoom-
  // OUT below 1 is bounded separately by TILE_EDIT_MIN_ZOOM in the wheel handler.
  return Math.max(1, Math.min(TILE_EDIT_MAX_ZOOM, cap));
}

/**
 * ANA-R4-TILE-02 — the SOURCE-image sub-region the tile canvas must re-draw so
 * the image zooms/pans IN LOCKSTEP with the editor geometry (drawing a smaller
 * source region into the same backing keeps it CRISP, unlike a CSS scale of the
 * already-downsampled crop). Composes the base crop with the local zoom/pan: the
 * visible region shrinks to `cropSide / z` and shifts by the pan, matching
 * exactly what {@link createTileTransform} maps screen→image to.
 */
export function zoomedTileCrop(
  frame: TileCropFrame,
  view: TileLocalView = { editZoom: 1, panX: 0, panY: 0, version: 0 },
): { sx: number; sy: number; cropSide: number } {
  const centre = frame.tileDisplayPx / 2;
  const z = clampEditZoom(view.editZoom);
  const scaleBase = frame.tileDisplayPx / frame.cropSide; // base-tile px / src px
  // base-tile coords of the tile's top-left corner (screen 0,0) under the view.
  const baseX0 = (0 - centre) / z - view.panX + centre;
  const baseY0 = (0 - centre) / z - view.panY + centre;
  return {
    sx: baseX0 / scaleBase + frame.sx,
    sy: baseY0 / scaleBase + frame.sy,
    cropSide: frame.cropSide / z,
  };
}

/**
 * Apply a wheel-zoom step that keeps the SCREEN point `(focusX, focusY)`
 * anchored (the image point under the cursor stays under the cursor). Returns
 * the next `{ editZoom, panX, panY }`. `delta` is the raw wheel deltaY (we zoom
 * in on negative delta — scroll up = zoom in, the conventional direction).
 * `maxZoom` caps zoom-in at the Tiles max-zoom setting (ANA-R4-TILE-02); default
 * is the editor's absolute band ceiling.
 */
export function applyTileWheelZoom(
  view: TileLocalView,
  frame: TileCropFrame,
  delta: number,
  focusX: number,
  focusY: number,
  maxZoom: number = TILE_EDIT_MAX_ZOOM,
): { editZoom: number; panX: number; panY: number } {
  const centre = frame.tileDisplayPx / 2;
  const oldZ = clampEditZoom(view.editZoom);
  // ~10% per wheel notch; clamp to the editor's band AND the max-zoom cap.
  const factor = delta < 0 ? 1.1 : 1 / 1.1;
  const cap = Math.max(
    TILE_EDIT_MIN_ZOOM,
    Math.min(TILE_EDIT_MAX_ZOOM, maxZoom),
  );
  const newZ = Math.max(TILE_EDIT_MIN_ZOOM, Math.min(cap, oldZ * factor));
  if (newZ === oldZ) {
    return { editZoom: oldZ, panX: view.panX, panY: view.panY };
  }
  // The base-tile point under the cursor must be invariant across the zoom:
  //   base = (focus − centre)/z − pan + centre   (same for old + new)
  // Solve the new pan so `base` is unchanged.
  const baseUnderCursor = (focusX - centre) / oldZ - view.panX + centre;
  const baseUnderCursorY = (focusY - centre) / oldZ - view.panY + centre;
  const panX = (focusX - centre) / newZ + centre - baseUnderCursor;
  const panY = (focusY - centre) / newZ + centre - baseUnderCursorY;
  return { editZoom: newZ, panX, panY };
}
